import React from 'react';

const FEATURE_SUGGESTIONS = [
  'Investment Options',
  'Insurance',
  'Fees',
  'Platform Features',
  'Reporting',
  'Online Access',
];

const StepProductComparison = ({ formData, updateFormData, nextStep, prevStep, disabled }) => {
  const data = formData.productComparison;

  const handleChange = (field, value) => {
    updateFormData('productComparison', { [field]: value });
  };

  const addComparison = (feature = '') => {
    const comparisons = [...(data.comparisons || []), { feature, current: '', proposed: '' }];
    handleChange('comparisons', comparisons);
  };

  const updateComparison = (index, field, value) => {
    const comparisons = [...(data.comparisons || [])];
    comparisons[index] = { ...comparisons[index], [field]: value };
    handleChange('comparisons', comparisons);
  };

  const removeComparison = (index) => {
    const comparisons = (data.comparisons || []).filter((_, i) => i !== index);
    handleChange('comparisons', comparisons);
  };

  const addSuggestion = (feature) => {
    const existing = (data.comparisons || []).some(
      (c) => c.feature.toLowerCase() === feature.toLowerCase()
    );
    if (!existing) {
      addComparison(feature);
    }
  };

  return (
    <fieldset disabled={disabled}>
      <h4 className="soa-step-title">Product Comparison</h4>
      <p className="soa-step-subtitle">
        Compare the current and proposed products to demonstrate the basis for any product replacement
      </p>

      {/* Current Product */}
      <div className="soa-section-banner">
        <i className="bi bi-box-arrow-left"></i>
        Current Product
      </div>

      <div className="row">
        <div className="col-md-6 soa-form-group">
          <label>Current Product Name</label>
          <input
            type="text"
            className="form-control"
            value={data.currentProduct}
            onChange={(e) => handleChange('currentProduct', e.target.value)}
            placeholder="e.g. AustralianSuper Balanced"
          />
        </div>
        <div className="col-md-6 soa-form-group">
          <label>Current Product Fees</label>
          <input
            type="text"
            className="form-control"
            value={data.currentProductFees}
            onChange={(e) => handleChange('currentProductFees', e.target.value)}
            placeholder="e.g. 0.70% p.a. + $78 admin fee"
          />
        </div>
      </div>

      <div className="soa-form-group">
        <label>Current Product Features</label>
        <textarea
          className="form-control"
          value={data.currentProductFeatures}
          onChange={(e) => handleChange('currentProductFeatures', e.target.value)}
          placeholder="Describe key features of the current product..."
          rows={3}
        />
      </div>

      {/* Proposed Product */}
      <div className="soa-section-banner">
        <i className="bi bi-box-arrow-in-right"></i>
        Proposed Product
      </div>

      <div className="soa-form-group">
        <label>Proposed Product Features</label>
        <textarea
          className="form-control"
          value={data.proposedProductFeatures}
          onChange={(e) => handleChange('proposedProductFeatures', e.target.value)}
          placeholder="Describe key features of the proposed product..."
          rows={3}
        />
      </div>

      {/* Feature Comparison */}
      <div className="soa-section-banner">
        <i className="bi bi-arrow-left-right"></i>
        Feature Comparison
      </div>

      <div className="mb-2">
        <span className="small text-muted me-2">Quick add:</span>
        {FEATURE_SUGGESTIONS.map((feature) => (
          <button
            key={feature}
            type="button"
            className="btn btn-sm me-1 mb-1"
            style={{
              backgroundColor: 'var(--info-color)',
              color: '#fff',
              fontSize: '0.78rem',
            }}
            onClick={() => addSuggestion(feature)}
            disabled={disabled}
          >
            <i className="bi bi-plus me-1"></i>{feature}
          </button>
        ))}
      </div>

      {(data.comparisons || []).length > 0 && (
        <div className="table-responsive mb-3">
          <table className="table table-sm" style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
                <th style={{ width: '25%' }}>Feature</th>
                <th style={{ width: '30%' }}>Current</th>
                <th style={{ width: '30%' }}>Proposed</th>
                <th style={{ width: '15%' }}></th>
              </tr>
            </thead>
            <tbody>
              {(data.comparisons || []).map((row, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.feature}
                      onChange={(e) => updateComparison(idx, 'feature', e.target.value)}
                      placeholder="Feature"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.current}
                      onChange={(e) => updateComparison(idx, 'current', e.target.value)}
                      placeholder="Current product"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.proposed}
                      onChange={(e) => updateComparison(idx, 'proposed', e.target.value)}
                      placeholder="Proposed product"
                    />
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeComparison(idx)}
                      disabled={disabled}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        className="btn btn-outline-primary btn-sm mb-3"
        onClick={() => addComparison()}
        disabled={disabled}
      >
        <i className="bi bi-plus-lg me-1"></i>Add Comparison Row
      </button>

      {/* Reason for Replacement */}
      <div className="soa-section-banner">
        <i className="bi bi-question-circle"></i>
        Reason for Replacement
      </div>

      <div className="soa-form-group">
        <label>Why is the product replacement in the client's best interest?</label>
        <textarea
          className="form-control"
          value={data.replacementReason}
          onChange={(e) => handleChange('replacementReason', e.target.value)}
          placeholder="Explain why the proposed product is more suitable than the current product, considering fees, features, investment options, and the client's needs..."
          rows={5}
        />
      </div>

      <div className="soa-step-nav">
        <button className="btn btn-outline-secondary" onClick={prevStep}>
          <i className="bi bi-arrow-left me-1"></i>Back
        </button>
        <button className="btn btn-primary" onClick={nextStep}>
          Next: Fees &amp; Costs <i className="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </fieldset>
  );
};

export default StepProductComparison;
